import React from 'react';
import { AlertCircle, Calendar, FileText, Building2, User, DollarSign, Shield, CheckCircle } from 'lucide-react';

const AlertCard = ({ type, title, items, icon: Icon, color }) => {
  if (!items || items.length === 0) return null;
  
  return (
    <div className={`border rounded-xl p-3 ${color} bg-opacity-10`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className={color.replace('border-', 'text-').replace('/30', '')} />
        <h4 className="font-semibold text-sm">{title}</h4>
        <span className="text-xs ml-auto">{items.length} alert(s)</span>
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {items.slice(0, 5).map((item, idx) => (
          <div key={idx} className="text-xs flex justify-between items-start gap-2 border-t border-gray-700 pt-1.5">
            <div className="flex-1">
              {item.tenant_name && (
                <div className="flex items-center gap-1 text-gray-300">
                  <User size={10} />
                  <span>{item.tenant_name}</span>
                </div>
              )}
              {item.hangar_number && (
                <div className="flex items-center gap-1 text-gray-400 mt-0.5">
                  <Building2 size={10} />
                  <span>Hangar {item.hangar_number}</span>
                </div>
              )}
              {item.amount && (
                <div className="flex items-center gap-1 text-red-400 mt-0.5 font-semibold">
                  <DollarSign size={10} />
                  <span>{Number(item.amount).toLocaleString()} {item.currency || 'due'}</span>
                </div>
              )}
              {item.days_left !== undefined && (
                <div className="text-gray-500 mt-0.5">
                  {item.days_left <= 0 ? 'Expired' : `${item.days_left} days left`}
                </div>
              )}
            </div>
            {item.end_date && (
              <div className="text-right text-gray-500 whitespace-nowrap">
                <Calendar size={10} className="inline mr-1" />
                {new Date(item.end_date).toLocaleDateString()}
              </div>
            )}
          </div>
        ))}
        {items.length > 5 && (
          <div className="text-center text-gray-500 text-xs pt-1">
            +{items.length - 5} more
          </div>
        )}
      </div>
    </div>
  );
};

const HangarLeaseAlerts = ({ data }) => {
  const {
    expiringLeases = [],
    rentArrears = [],
    expiringInsurances = [],
    unpaidTaxes = []
  } = data;

  const hasAnyAlerts = expiringLeases.length > 0 || rentArrears.length > 0 || 
                       expiringInsurances.length > 0 || unpaidTaxes.length > 0;

  if (!hasAnyAlerts) {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
        <CheckCircle size={20} className="text-emerald-400 mx-auto mb-2" />
        <p className="text-emerald-400 text-sm font-medium">All clear!</p>
        <p className="text-gray-400 text-xs">No pending alerts for hangars & leases</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-white font-semibold text-sm flex items-center gap-2">
        <AlertCircle size={15} className="text-amber-400" />
        Alerts & Reminders
      </h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <AlertCard
          type="arrears"
          title="Rent Arrears"
          items={rentArrears}
          icon={AlertCircle}
          color="border-red-500/30 bg-red-500/5"
        />
        <AlertCard
          type="expiring"
          title="Leases Expiring Soon"
          items={expiringLeases}
          icon={Calendar}
          color="border-orange-500/30 bg-orange-500/5"
        />
        <AlertCard
          type="insurance"
          title="Insurance Expiring"
          items={expiringInsurances}
          icon={Shield}
          color="border-yellow-500/30 bg-yellow-500/5"
        />
        <AlertCard
          type="taxes"
          title="Unpaid Local Taxes"
          items={unpaidTaxes}
          icon={FileText}
          color="border-purple-500/30 bg-purple-500/5"
        />
      </div>
    </div>
  );
};

export default HangarLeaseAlerts;